import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { artists, products } from '@/utils/api';
import { Package } from 'lucide-react';
import { toast } from 'sonner';

const ArtistProfile = () => {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [artistProducts, setArtistProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArtistData();
  }, [id]);

  const loadArtistData = async () => {
    try {
      const [artistRes, productsRes] = await Promise.all([
        artists.getById(id),
        products.getAll({ artistId: id })
      ]);
      setArtist(artistRes.data);
      setArtistProducts(productsRes.data);
    } catch (error) {
      console.error('Error loading artist:', error);
      toast.error('Failed to load artist profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="artist-profile-page">
        <Navbar />
        <div className="section" style={{paddingTop: "180px"}}>
          <div className="text-center py-12">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!artist) {
    return (
      <div data-testid="artist-profile-page">
        <Navbar />
        <div className="section" style={{paddingTop: "180px"}}>
          <div className="text-center py-12">
            <p className="text-lg text-gray-500">Artist not found</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="artist-profile-page">
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <div className="card max-w-4xl mx-auto mb-12">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-48 h-48 bg-[#f5f5f5] rounded-full flex items-center justify-center">
                <span className="text-6xl text-[#ef4444]">
                  {artist.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1" data-testid="artist-info">
                <h1 className="text-4xl font-bold mb-4" data-testid="artist-name">{artist.name}</h1>
                {artist.category && (
                  <p className="text-lg text-[#ef4444] font-semibold mb-2" data-testid="artist-category">{artist.category}</p>
                )}
                {artist.bio && (
                  <p className="text-gray-600 mb-4" data-testid="artist-bio">{artist.bio}</p>
                )}
                <div className="flex flex-col gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2" data-testid="artist-products-count">
                    <Package className="w-4 h-4" />
                    <span>{artistProducts.length} Products Available</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-semibold mb-6" data-testid="products-title">
            Products by {artist.name}
          </h2>
          
          {artistProducts.length > 0 ? (
            <div className="product-grid" data-testid="artist-products-grid">
              {artistProducts.map((product) => (
                <Link
                  to={`/products/${product.id}`}
                  key={product.id}
                  className="product-card"
                  data-testid={`product-card-${product.id}`}
                >
                  <img
                    src={products.getImage(product.images[0])}
                    alt={product.title}
                    className="product-image"
                  />
                  <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    <p className="text-sm text-gray-500 mb-2">{product.category}</p>
                    <p className="product-price">₹{product.price.toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-products">
              <p className="text-gray-500 text-lg">No products available yet</p>
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ArtistProfile;